import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { createEffectFactory } from '@recruitee/ngrx';

import { ReferralsOfferAssignmentActions } from '../offer-assignment/offer-assignment.store';
import { ReferralsTemplateActions } from '../templates/templates.store';
import { TemplateItemHttpClient } from './template-item.http-client';
import { ReferralsTemplateItemActions } from './template-item.store';

@Injectable({ providedIn: 'root' })
export class ReferralsTemplateItemEffects {
  public effects = createEffectFactory(ReferralsTemplateItemActions, this.actions$);

  public fetch$ = this.effects(ReferralsTemplateItemActions.fetch, ({ payload }) =>
    this.http.loadTemplate(payload),
  );

  public update$ = this.effects(ReferralsTemplateItemActions.update, ({ payload }) =>
    this.http.updateTemplate(payload),
  );

  public setDefault$ = this.effects(ReferralsTemplateItemActions.setDefault, ({ payload }) =>
    this.http.setAsDefault(payload).pipe(
      map(data => ReferralsTemplateItemActions.setDefaultSuccess({ payload: { data } })),
      catchError(error => of(ReferralsTemplateItemActions.setDefaultFailed({ payload: error }))),
    ),
  );

  public createOrUpdateCustomTemplate$ = this.effects(
    ReferralsTemplateItemActions.createOrUpdateCustomTemplate,
    ({ payload }) => {
      const { offerId, data, isCustomAssigned } = payload;

      return this.http.createOrUpdateCustomTemplate(offerId, data, isCustomAssigned).pipe(
        map(data =>
          ReferralsTemplateItemActions.createOrUpdateCustomTemplateSuccess({
            payload: { offerId, data },
          }),
        ),
        catchError(error =>
          of(
            ReferralsTemplateItemActions.createOrUpdateCustomTemplateFailed({
              payload: error,
            }),
          ),
        ),
      );
    },
  );

  public delete$ = this.effects(ReferralsTemplateItemActions.delete, ({ payload }) =>
    this.http.deleteTemplate(payload).pipe(
      map(data => ReferralsTemplateItemActions.deleteSuccess({ payload: data })),
      catchError(error => of(ReferralsTemplateItemActions.deleteFailed({ payload: error }))),
    ),
  );

  public saveAsTemplate$ = this.effects(
    ReferralsTemplateItemActions.saveAsTemplate,
    ({ payload }) => {
      const { name, category, offerId } = payload;
      return this.http.saveAsTemplate(name, category, offerId).pipe(
        map(data => ReferralsTemplateItemActions.saveAsTemplateSuccess({ payload: { data } })),
        catchError(error =>
          of(ReferralsTemplateItemActions.saveAsTemplateFailed({ payload: error })),
        ),
      );
    },
  );

  public create$ = this.effects(ReferralsTemplateItemActions.create, ({ payload }) =>
    this.http.createTemplate(payload).pipe(
      map(data => ReferralsTemplateItemActions.createSuccess({ payload: { data } })),
      catchError(error => of(ReferralsTemplateItemActions.createFailed({ payload: error }))),
    ),
  );

  public refreshTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ReferralsTemplateItemActions.updateSuccess,
        ReferralsTemplateItemActions.setDefaultSuccess,
        ReferralsTemplateItemActions.deleteSuccess,
        ReferralsTemplateItemActions.createSuccess,
      ),
      switchMap(() => [ReferralsTemplateActions.fetchCollection()]),
    ),
  );

  public refreshTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferralsTemplateItemActions.setDefaultSuccess),
      switchMap(({ payload }) => [
        ReferralsTemplateItemActions.fetch({ payload: payload.data.id }),
      ]),
    ),
  );

  public refreshOfferAssignment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReferralsTemplateItemActions.createOrUpdateCustomTemplateSuccess),
      switchMap(({ payload }) => [
        ReferralsOfferAssignmentActions.fetchOne({
          payload: { id: payload.offerId.toString() },
        }),
      ]),
    ),
  );

  constructor(private actions$: Actions, private http: TemplateItemHttpClient) {}
}
